import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, forwardRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MessageService } from 'base';
import { ConfigurationService } from '../../services/configuration.service';
import { OnDestroy } from '@angular/core';
import { ComponentBase } from 'common';
import { StringUtils } from 'DiscoveryEngine.TS/Utils';
import { Integration, IntegrationNames, IntegrationTable } from '../../../DiscoveryEngine.TS/Core/Integration';
import { DefaultIntegrationSettingsChild } from '../default-integration-settings-child-interface/default-integration-settings-child-interface';


@Component({
	selector: 'service-now-default-settings',
	templateUrl: './service-now-default-settings.component.html',
	styleUrls: ['./service-now-default-settings.component.scss']
})
export class ServiceNowDefaultSettingsComponent extends ComponentBase implements DefaultIntegrationSettingsChild, OnInit, OnDestroy, OnChanges {
	@Input()
	public inputSettings: Integration;

	public form: UntypedFormGroup;

	public enabled: UntypedFormControl;

	public serverURL: UntypedFormControl;
	public tokenURL: UntypedFormControl;
	public clientID: UntypedFormControl;
	public clientSecret: UntypedFormControl;
	public username: UntypedFormControl;
	public password: UntypedFormControl;

	private clientSecretChanged: boolean;
	private clientSecretChangedSub: Subscription;

	private passwordChanged: boolean;
	private passwordChangedSub: Subscription;

	constructor(private fb: UntypedFormBuilder,
		private configService: ConfigurationService,
		private messageService: MessageService) {

		super();
	}

	public ngOnInit(): void {
		this.buildForm();
	}

	public ngOnDestroy(): void {
		if (this.passwordChangedSub) {
			this.passwordChangedSub.unsubscribe();
		}

		if (this.clientSecretChangedSub) {
			this.clientSecretChangedSub.unsubscribe();
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.buildForm();
	}

	private buildForm(): void {
		this.enabled = undefined;
		this.serverURL = undefined;
		this.tokenURL = undefined;
		this.clientID = undefined;
		this.clientSecret = undefined;
		this.username = undefined;
		this.password = undefined;

		let enabled = true;
		let serverURL = "";
		let tokenURL = "";
		let clientID = "";
		let clientSecret = "";
		let username = "";
		let password = "";

		// Load in the settings from the input settings variable, and set them to the form controls.
		if (this.inputSettings) {
			enabled = this.inputSettings.Enabled;
			serverURL = this.inputSettings.ServerURL;
			tokenURL = this.inputSettings.TokenServerURL;
			clientID = this.inputSettings.ClientID;
			clientSecret = (this.inputSettings.HasClientSecret ? this.hidePassword(this.inputSettings.ClientSecret) : "");
			username = this.inputSettings.Username;
			password = (this.inputSettings.HasPassword ? this.hidePassword(this.inputSettings.Password) : "");
		}

		this.enabled = new UntypedFormControl(enabled);
		this.serverURL = new UntypedFormControl(serverURL);
		this.tokenURL = new UntypedFormControl(tokenURL);
		this.clientID = new UntypedFormControl(clientID);
		this.clientSecret = new UntypedFormControl(clientSecret);
		this.username = new UntypedFormControl(username);
		this.password = new UntypedFormControl(password);

		this.passwordChanged = false;
		this.passwordChangedSub = this.password.valueChanges.subscribe(() => {
			this.passwordChanged = true;
		});

		this.clientSecretChanged = false;
		this.clientSecretChangedSub = this.clientSecret.valueChanges.subscribe(() => {
			this.clientSecretChanged = true;
		});

		this.form = this.fb.group({
			enabled: this.enabled,
			serverURL: this.serverURL,
			tokenURL: this.tokenURL,
			clientID: this.clientID,
			clientSecret: this.clientSecret,
			username: this.username,
			password: this.password,
		},
		{
			validator: Validators.compose([
				this.noneOrAllSettings(),
			])
		});
	}

	public enableSave(): boolean {
		return !this.form.invalid;
	}

	public onSave(): void {
		const outputIntegrationTable = new IntegrationTable();
		const integrationSettings = this.getIntegrationSettings();
		outputIntegrationTable.Add(integrationSettings);
		this.configService.setIntegrationTable(outputIntegrationTable).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully saved ServiceNow default settings");
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to save ServiceNow default settings");
			});
	}

	public onClientSecretKeyDownHandler(event: any): void {
		if (!this.clientSecretChanged) {
			this.clientSecretChanged = true;

			this.clientSecret.setValue('');
			this.clientSecret.markAsDirty();
		}
	}

	public onPasswordKeyDownHandler(event: any): void {
		if (!this.passwordChanged) {
			this.passwordChanged = true;

			this.password.setValue('');
			this.password.markAsDirty();
		}
	}

	public getIntegrationSettings(): Integration {
		const outputIntegration = new Integration(IntegrationNames.ServiceNow);
		outputIntegration.Enabled = this.enabled.value;
		outputIntegration.ServerURL = this.serverURL.value;
		outputIntegration.TokenServerURL = this.tokenURL.value;
		outputIntegration.ClientID = this.clientID.value;
		if (this.clientSecretChanged) {
			outputIntegration.ClientSecret = StringUtils.ObfuscateString2(this.clientSecret.value);
		}
		else {
			outputIntegration.ClientSecret = this.inputSettings.ClientSecret;
		}
		outputIntegration.Username = this.username.value;
		if (this.passwordChanged) {
			outputIntegration.Password = StringUtils.ObfuscateString2(this.password.value);
		}
		else {
			outputIntegration.Password = this.inputSettings.Password;
		}

		return outputIntegration;
	}

	private noneOrAllSettings(): ValidatorFn {
		return () => {
			let b1 = (!!this.serverURL.value !== !!this.tokenURL.value);
			let b2 = (!!this.tokenURL.value !== !!this.clientID.value);
			let b3 = (!!this.clientID.value !== !!this.clientSecret.value);
			let b4 = (!!this.clientSecret.value !== !!this.username.value);
			let b5 = (!!this.username.value !== !!this.password.value);
			if (b1 || b2 || b3 || b4 || b5) {
				return {
					noneOrAllSettings: { valid: false }
				};
			}

			return null;
		}
	}

}
